import { useEffect, useState } from "react";

export type AnchorRect = {
	top: number;
	left: number;
	width: number;
	height: number;
};

const findAnchor = (anchor: string) =>
	document.querySelector<HTMLElement>(`[data-experience-anchor="${anchor}"]`);

export const useAnchorRect = (anchor: string): AnchorRect | undefined => {
	const [rect, setRect] = useState<AnchorRect | undefined>(undefined);

	useEffect(() => {
		const element = findAnchor(anchor);
		if (!element) {
			setRect(undefined);
			return;
		}

		const measure = () => {
			const box = element.getBoundingClientRect();
			setRect((previous) =>
				previous &&
				previous.height === box.height &&
				previous.left === box.left &&
				previous.top === box.top &&
				previous.width === box.width
					? previous
					: {
							height: box.height,
							left: box.left,
							top: box.top,
							width: box.width,
						},
			);
		};

		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		window.addEventListener("resize", measure);
		window.addEventListener("scroll", measure, true);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measure);
			window.removeEventListener("scroll", measure, true);
		};
	}, [anchor]);

	return rect;
};

// WHY not disabled: a step must not tell the user to use a control they cannot operate.
export const anchorExists = (anchor: string) => {
	const element = findAnchor(anchor);
	return element !== null && !element.matches(":disabled");
};
